import { zodResolver } from "@hookform/resolvers/zod";
import { composeProgress, isLoading } from "apollo/utils";
import { useExtractDocs } from "context/ExtractDocsContext";
import {
  DISCHARGE_LETTER_DOCS_FORM_TO_PATIENT_DOCUMENT,
  DOCS_FORM_NAMES,
  EXTRACT_OCR_STATUS,
  PATIENT_DOCUMENTS,
  QUERY_PROGRESS_FAILED,
  QUERY_PROGRESS_NOT_STARTED,
  QUERY_PROGRESS_PENDING,
  QUERY_PROGRESS_SUCCEED,
  RECARE_FILE_TYPE,
  REHAB_FORM_TO_PATIENT_DOCUMENT,
  SOURCE_DOCUMENT_SOURCES,
} from "core/consts";
import { useIsDocsAndExtractActive } from "core/model/careseekers";
import { isFileHISStateful } from "core/model/files/utils";
import { useGetOntology } from "core/model/utils/ontologies/hooks";
import {
  Auction,
  AuctionForm,
  ClinicalConsultation,
  Contradictions,
  DocsFormDataMapped,
  DocsFormName,
  DocsFormStatusResponse,
  FileState,
  FormMetadata,
  Patient,
  QueryProgress,
  RehabFormName,
  SourceFields,
} from "core/types";
import {
  CreateRehaPdfProps,
  useCreateRehaPdf,
} from "dsl/ecosystems/PatientForms/RehabForms/useCreateRehaPdf";
import { RehabFormDataMapped } from "dsl/ecosystems/PatientForms/RehabForms/utils";
import {
  CreateTransitionalCarePdfProps,
  useCreateTransitionalCarePdf,
} from "dsl/ecosystems/PatientForms/useCreateTransitionalCarePdf";
import { useDocumentHandling } from "dsl/ecosystems/PatientForms/useDocumentHandling";
import { useImportFormData } from "dsl/ecosystems/PatientForms/utils";
import {
  isDischargeLetterFormName,
  MigratedForms,
  useSelectSourceDocumentFile,
} from "dsl/ecosystems/RecareDocs/Modal/utils";
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { flushSync } from "react-dom";
import { Path, PathValue, useForm } from "react-hook-form";
import { useTranslations } from "translations";
import { z } from "zod";
import { DOCS_FORM_FOOTER_SCREENS, DocsFormFooterScreen } from ".";
import { FOOTER_BANNERS, FooterBannerType } from "./Footer";
import { FormFieldConfig, PdfConfig, useGetFormFieldConfig } from "./Forms";
import { buildDischargeLetterObservation } from "./Forms/DischargeLetters/buildObservation";
import {
  CreateDischargePdfProps,
  useCreateFormPdf,
} from "./Forms/DischargeLetters/useCreateFormPdf";
import { getAuctionForm } from "./Forms/DischargeLetters/useGetForm";
import { HEADER_BANNERS, HeaderBannerAction } from "./HeaderBanners";
import { useDeleteConfirmation } from "./onDelete";
import { useDocsFillForm } from "./useDocsFillForm";
import { useDocsFormOnSubmit } from "./useDocsFormOnSubmit";

// to be deprecated in favour of joint docs fetch
export function useGetFormData<T extends DocsFormName>({
  auctionForms,
  consultationForms,
  formName,
}: {
  formName: T;
  auctionForms: AuctionForm[];
  consultationForms: AuctionForm[] | undefined;
}): {
  form: AuctionForm | null | undefined;
  formData: DocsFormDataMapped<T> | null | undefined;
  metadata?: FormMetadata<T> | undefined;
} {
  return useMemo(() => {
    const forms = auctionForms?.length ? auctionForms : consultationForms ?? [];

    return getAuctionForm({
      auctionForms: forms,
      formName,
    }) as {
      form: AuctionForm | null | undefined;
      formData: DocsFormDataMapped<T> | null | undefined;
      metadata?: FormMetadata<T> | undefined;
    };
  }, [formName, auctionForms, consultationForms]);
}

function usePdfHandlers<T extends DocsFormName>({
  auctionForms,
  config,
  files,
  footerScreen,
  formData,
  formName,
  formType,
  isDirty,
  patient,
  setFooterBanner,
  setFooterScreen,
}: {
  auctionForms: AuctionForm[];
  files: { name: string; file: FileState }[];
  footerScreen: DocsFormFooterScreen;
  formData: DocsFormDataMapped<T> | undefined;
  formName: T;
  formType: number | null | undefined;
  isDirty: boolean;
  patient: Patient;
  setFooterBanner: (banner: FooterBannerType | null) => void;
  setFooterScreen: Dispatch<SetStateAction<DocsFormFooterScreen>>;
  config: FormFieldConfig<T>;
}) {
  const translations = useTranslations();
  const externalId = patient.external_id ?? "";
  const urlCache = useRef(new Map<string, string>());
  const selectSourceDocumentFile = useSelectSourceDocumentFile({
    canUseExtractedDocument: false,
    externalId,
  });

  const matchedFile = useMemo(
    () =>
      files.find(
        ({ file }) =>
          "recare_file_type" in file &&
          file.recare_file_type === formType &&
          file.form_synced_with_file,
      ),
    [files, formType],
  );

  const getExistingFileUrl = useCallback(async () => {
    if (!matchedFile) return undefined;

    const result = await selectSourceDocumentFile({
      files,
      sourceDocument: {
        filename: matchedFile.name,
        id: String(matchedFile.file.id),
        is_deleted: false,
        kis_file_id: isFileHISStateful(matchedFile.file)
          ? matchedFile.file.id
          : null,
        recare_file_id: !isFileHISStateful(matchedFile.file)
          ? matchedFile.file.id
          : null,
        source: SOURCE_DOCUMENT_SOURCES.RECARE_FILE,
        url: "",
      },
      urlCache: urlCache.current,
    });
    return result?.url;
  }, [matchedFile, files, selectSourceDocumentFile]);

  const createRehaPdf = useCreateRehaPdf();
  const createTransitionalCarePdf = useCreateTransitionalCarePdf();

  const createFormPdf = useCreateFormPdf({
    auctionForms,
    externalId,
    formName,
  });

  const createDischargePdf = useCallback(
    async ({
      formData: data,
    }: {
      formData: DocsFormDataMapped<MigratedForms>;
    }) => {
      return createFormPdf({
        documentNumber: -1,
        formData: data,
        pdfConfig: config.pdfConfig as PdfConfig<MigratedForms>,
      });
    },
    [config.pdfConfig, createFormPdf],
  );

  const transitionalCareHandlers =
    useDocumentHandling<CreateTransitionalCarePdfProps>({
      careseekerName: patient.careseeker?.name ?? "",
      createPDFDocument: createTransitionalCarePdf,
      externalId,
      getExistingFileUrl: matchedFile ? getExistingFileUrl : undefined,
      userId: patient.user_id ?? "",
    });

  const rehaHandlers = useDocumentHandling<CreateRehaPdfProps>({
    careseekerName: patient.careseeker?.name ?? "",
    createPDFDocument: createRehaPdf,
    externalId,
    getExistingFileUrl: matchedFile ? getExistingFileUrl : undefined,
    userId: patient.user_id ?? "",
  });

  const dischargeHandlers = useDocumentHandling<CreateDischargePdfProps>({
    careseekerName: patient.careseeker?.name ?? "",
    createPDFDocument: createDischargePdf,
    externalId,
    getExistingFileUrl: matchedFile ? getExistingFileUrl : undefined,
    userId: patient.user_id ?? "",
  });

  const isDischargeLetterForm = isDischargeLetterFormName(formName);

  const transitionalCareForm = auctionForms?.find(
    (form) => form.form_type === RECARE_FILE_TYPE.TRANSITIONAL_CARE,
  );

  const handleOpenPdf = useCallback(async () => {
    if (isDirty && footerScreen !== DOCS_FORM_FOOTER_SCREENS.WARN_OPEN_PDF) {
      setFooterScreen(DOCS_FORM_FOOTER_SCREENS.WARN_OPEN_PDF);
      return;
    }
    if (!matchedFile) return;
    let ok = false;
    if (formName === DOCS_FORM_NAMES.TRANSITIONAL_CARE) {
      ok = await transitionalCareHandlers.handleOpenPdf();
    } else if (isDischargeLetterForm) {
      ok = await dischargeHandlers.handleOpenPdf();
    } else {
      ok = await rehaHandlers.handleOpenPdf();
    }
    if (!ok) {
      setFooterBanner(FOOTER_BANNERS.OPEN_PDF_ERROR);
      return;
    }
    if (footerScreen === DOCS_FORM_FOOTER_SCREENS.WARN_OPEN_PDF) {
      setFooterScreen(DOCS_FORM_FOOTER_SCREENS.DEFAULT);
    }
  }, [
    isDirty,
    footerScreen,
    setFooterScreen,
    matchedFile,
    formName,
    isDischargeLetterForm,
    dischargeHandlers,
    setFooterBanner,
    transitionalCareHandlers,
    rehaHandlers,
  ]);

  const handleSendToHis = useCallback(async () => {
    if (isDirty && footerScreen !== DOCS_FORM_FOOTER_SCREENS.WARN_SEND_TO_HIS) {
      setFooterScreen(DOCS_FORM_FOOTER_SCREENS.WARN_SEND_TO_HIS);
      return;
    }

    let ok = false;
    if (formName === DOCS_FORM_NAMES.TRANSITIONAL_CARE) {
      if (!transitionalCareForm) {
        setFooterBanner(FOOTER_BANNERS.SEND_TO_HIS_ERROR);
        return;
      }
      ok = await transitionalCareHandlers.handleSendToHis({
        documentType: PATIENT_DOCUMENTS.TRANSITIONAL_CARE,
        transitionalCare: transitionalCareForm,
      });
    } else if (isDischargeLetterForm) {
      const dischargeFormData = formData as DocsFormDataMapped<MigratedForms>;
      const dischargeFormName = formName;
      const observation = buildDischargeLetterObservation(
        dischargeFormData,
        dischargeFormName,
        translations,
        externalId,
      );
      ok = await dischargeHandlers.handleSendToHis({
        documentType:
          DISCHARGE_LETTER_DOCS_FORM_TO_PATIENT_DOCUMENT[dischargeFormName],
        formData: dischargeFormData,
        observation,
      });
    } else {
      ok = await rehaHandlers.handleSendToHis({
        auctionForms,
        documentNumber: -1,
        documentType: REHAB_FORM_TO_PATIENT_DOCUMENT[formName as RehabFormName],
        externalId,
        formData: formData as RehabFormDataMapped<RehabFormName>,
        formName: formName as RehabFormName,
      });
    }

    if (!ok) {
      setFooterBanner(FOOTER_BANNERS.SEND_TO_HIS_ERROR);
      return;
    }
    if (footerScreen === DOCS_FORM_FOOTER_SCREENS.WARN_SEND_TO_HIS) {
      setFooterScreen(DOCS_FORM_FOOTER_SCREENS.DEFAULT);
    }
    setFooterBanner(FOOTER_BANNERS.SEND_TO_HIS_SUCCESS);
  }, [
    isDirty,
    footerScreen,
    setFooterScreen,
    formName,
    isDischargeLetterForm,
    transitionalCareForm,
    transitionalCareHandlers,
    dischargeHandlers,
    rehaHandlers,
    auctionForms,
    formData,
    externalId,
    translations,
    setFooterBanner,
  ]);

  const progressOpenPdf = composeProgress([
    transitionalCareHandlers.progressOpenPdf,
    dischargeHandlers.progressOpenPdf,
    rehaHandlers.progressOpenPdf,
  ]);

  const progressSendToHis = composeProgress([
    transitionalCareHandlers.progressSendToHis,
    dischargeHandlers.progressSendToHis,
    rehaHandlers.progressSendToHis,
  ]);

  return { handleOpenPdf, handleSendToHis, progressOpenPdf, progressSendToHis };
}

export type SourceSelected<T extends DocsFormName> = {
  contradictions?: Contradictions;
  fieldName: keyof DocsFormDataMapped<T>;
  sourceFields: SourceFields;
};

const useSelectedSource = <T extends DocsFormName>({
  docsFillFormResult,
  headerBannerAction,
  savedMetadata,
}: {
  docsFillFormResult: DocsFormStatusResponse | null;
  savedMetadata: FormMetadata<T> | undefined;
  headerBannerAction: HeaderBannerAction;
}) => {
  const [selectedSource, setSelectedSource] = useState<
    SourceSelected<T> | undefined
  >(undefined);

  const contradictions = useMemo(() => {
    const savedParsed =
      typeof savedMetadata?.contradictions?.decrypted === "string"
        ? (() => {
            try {
              return JSON.parse(
                savedMetadata.contradictions.decrypted,
              ) as Record<string, Contradictions>;
            } catch {
              return {};
            }
          })()
        : {};

    return Object.entries(docsFillFormResult?.form ?? {}).reduce<
      Record<string, Contradictions | undefined>
    >(
      (acc, [key, inferredValue]) => {
        const inferredContradictions = inferredValue.contradictions;
        return {
          ...acc,
          [key]: inferredContradictions ?? savedParsed[key] ?? undefined,
        };
      },
      { ...savedParsed },
    );
  }, [docsFillFormResult?.form, savedMetadata]);

  const sourceFields = useMemo(() => {
    const merged = Object.entries(docsFillFormResult?.form ?? {}).reduce(
      (acc, [key, inferredValue]) => {
        const savedFieldMetadata =
          savedMetadata?.source_fields?.[key as keyof DocsFormDataMapped<T>];
        const inferredSourceDocuments = inferredValue.source_documents;
        return {
          ...acc,
          [key]: {
            ...(savedFieldMetadata ?? {}),
            manually_edited:
              inferredValue.value != null &&
              headerBannerAction.banner !== HEADER_BANNERS.IMPORT_WARNING
                ? false
                : !!savedFieldMetadata?.manually_edited,
            source_documents:
              inferredSourceDocuments ??
              savedFieldMetadata?.source_documents ??
              [],
          },
        };
      },
      (savedMetadata?.source_fields ?? {}) as FormMetadata<T>["source_fields"],
    );

    return merged;
  }, [docsFillFormResult?.form, headerBannerAction, savedMetadata]);

  const handleSetSelectedSource = useCallback(
    (fieldName: keyof DocsFormDataMapped<T>) => {
      setSelectedSource({
        contradictions: contradictions[fieldName],
        fieldName,
        sourceFields: sourceFields[fieldName] ?? {},
      });
    },
    [contradictions, sourceFields],
  );

  const isSourceSelected = useCallback(
    (fieldName: keyof DocsFormDataMapped<T>) => {
      return selectedSource?.fieldName === fieldName;
    },
    [selectedSource],
  );

  const getSourceFields = useCallback(
    (fieldName: keyof DocsFormDataMapped<T>) => {
      return sourceFields[fieldName] ?? {};
    },
    [sourceFields],
  );

  const getContradictionFields = useCallback(
    (fieldName: keyof DocsFormDataMapped<T>) => {
      return contradictions[fieldName as string];
    },
    [contradictions],
  );

  const resetSelectedSource = useCallback(() => {
    setSelectedSource(undefined);
  }, []);

  return {
    contradictions,
    getContradictionFields,
    getSourceFields,
    handleSetSelectedSource,
    isSourceSelected,
    resetSelectedSource,
    selectedSource,
    sourceFields,
  } as const;
};

export type SourceInformation<T extends DocsFormName> = ReturnType<
  typeof useSelectedSource<T>
>;

const useFooterBanner = () => {
  const [footerBanner, setFooterBanner] = useState<FooterBannerType | null>(
    null,
  );

  const footerBannerTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleSetFooterBanner = useCallback(
    (banner: FooterBannerType | null) => {
      if (footerBannerTimeoutRef.current) {
        clearTimeout(footerBannerTimeoutRef.current);
      }

      setFooterBanner(banner);

      if (banner) {
        footerBannerTimeoutRef.current = setTimeout(() => {
          setFooterBanner(null);
        }, 5000);
      }
    },
    [],
  );

  useEffect(() => {
    return () => {
      if (footerBannerTimeoutRef.current) {
        clearTimeout(footerBannerTimeoutRef.current);
      }
    };
  }, []);

  return { footerBanner, setFooterBanner: handleSetFooterBanner };
};

export function useDocsForm<T extends DocsFormName>({
  auction,
  auctionForms,
  consultation,
  files,
  formName,
  onAfterDelete,
  patient,
}: {
  auction: Auction | undefined;
  auctionForms: AuctionForm[];
  consultation: ClinicalConsultation | undefined;
  files: { name: string; file: FileState }[];
  formName: T;
  onAfterDelete?: () => void;
  patient: Patient;
}) {
  const getOntology = useGetOntology();
  const translations = useTranslations();
  const [headerBannerAction, setHeaderBannerAction] =
    useState<HeaderBannerAction>({
      banner: null,
    });

  const [footerScreen, setFooterScreen] = useState<DocsFormFooterScreen>(
    DOCS_FORM_FOOTER_SCREENS.DEFAULT,
  );
  const { footerBanner, setFooterBanner } = useFooterBanner();

  const [importFormProgress, setImportFormProgress] = useState<QueryProgress>(
    QUERY_PROGRESS_NOT_STARTED,
  );
  const { extractOcrStatus } = useExtractDocs();
  const { isDischargeLetterActive, isDocsAndExtractActive } =
    useIsDocsAndExtractActive();

  const noDocumentsExtracted = !extractOcrStatus.result?.documents?.some(
    (doc) => doc.status === EXTRACT_OCR_STATUS.EXTRACTED,
  );

  const {
    form,
    formData,
    metadata: savedMetadata,
  } = useGetFormData({
    auctionForms,
    consultationForms: consultation?.forms ?? [],
    formName,
  });

  const hasPreviousFormValue = !!formData;

  const [confirmationChecked, setConfirmationChecked] = useState(false);
  const [formDataState, setFormDataState] = useState<
    DocsFormDataMapped<T> | undefined
  >(undefined);

  const { config, skipRequiredValidation } = useGetFormFieldConfig(
    formName,
    translations,
  );

  const initialValues = config.getInitialValue({
    auctionForms,
    patient,
    value: formData,
  });

  const methods = useForm<DocsFormDataMapped<T>>({
    defaultValues: initialValues,
    disabled:
      footerScreen !== DOCS_FORM_FOOTER_SCREENS.DEFAULT ||
      isLoading(importFormProgress),
    resolver: zodResolver(
      config.schema as z.ZodType<DocsFormDataMapped<T>, DocsFormDataMapped<T>>,
    ),
  });

  const onError = useCallback(() => {
    setImportFormProgress(QUERY_PROGRESS_FAILED);
    setHeaderBannerAction({ banner: HEADER_BANNERS.EXTRACT_FAILED });
  }, []);

  const onPending = useCallback(() => {
    setImportFormProgress(QUERY_PROGRESS_PENDING);
    setHeaderBannerAction({ banner: HEADER_BANNERS.EXTRACTING });
  }, []);

  const onSuccess = useCallback(
    (result: DocsFormStatusResponse) => {
      if (!result.form_stats?.filled_fields) {
        setImportFormProgress(QUERY_PROGRESS_SUCCEED);
        setHeaderBannerAction({
          banner: HEADER_BANNERS.EXTRACT_COMPLETED_NONE_FILLED,
        });
        return;
      }

      if (
        hasPreviousFormValue &&
        headerBannerAction.banner !== HEADER_BANNERS.IMPORT_WARNING
      ) {
        // check whether we are going to overwrite something, if so show the overwrite warning
        const overwriteFields = Object.entries(result.form).filter(
          ([field, { value }]) => {
            if (value == null || value === "") return false;
            const currentValue = methods.getValues(
              field as Path<DocsFormDataMapped<T>>,
            );
            return currentValue && currentValue !== value;
          },
        );

        if (overwriteFields.length > 0) {
          setImportFormProgress(QUERY_PROGRESS_SUCCEED);
          setHeaderBannerAction({ banner: HEADER_BANNERS.IMPORT_WARNING });
          return;
        }
      }
      setHeaderBannerAction({
        banner: HEADER_BANNERS.EXTRACT_COMPLETED,
        context: {
          numFilledFields: result.form_stats.filled_fields,
        },
      });
      flushSync(() => {
        setImportFormProgress(QUERY_PROGRESS_SUCCEED);
      });
      Object.entries(result.form).forEach(([field, { value }]) => {
        if (value == null || value === "") return;
        methods.setValue(
          field as Path<DocsFormDataMapped<T>>,
          value as PathValue<
            DocsFormDataMapped<T>,
            Path<DocsFormDataMapped<T>>
          >,
          {
            shouldDirty: true,
          },
        );
      });
    },
    [hasPreviousFormValue, headerBannerAction, methods],
  );

  const {
    fillForm: docsFillForm,
    resetResult: resetDocsFillFormResult,
    result: docsFillFormResult,
  } = useDocsFillForm({
    auctionId: auction?.id,
    careseekerId: patient.careseeker?.id,
    consultationId: consultation?.id,
    externalId: patient.external_id,
    onError,
    onPending,
    onSuccess,
    patientId: patient.id,
    skip: noDocumentsExtracted,
  });

  const sourceInformation = useSelectedSource<T>({
    docsFillFormResult,
    headerBannerAction,
    savedMetadata,
  });

  const { handleAuctionToFormData, importFormData } = useImportFormData({
    auction,
    formGetters: config.auctionToForm?.(getOntology, translations),
    formOnChange: (value, field) => {
      if (value == null || value === "") return;
      methods.setValue(field as Path<DocsFormDataMapped<T>>, value, {
        shouldDirty: true,
      });
    },
    formValueGetter: (field) => {
      return methods.getValues(field as Path<DocsFormDataMapped<T>>);
    },
  });

  const handleImportData = async () => {
    try {
      setImportFormProgress(QUERY_PROGRESS_PENDING);
      const { formData, isOverwriting } = handleAuctionToFormData();
      if (
        hasPreviousFormValue &&
        headerBannerAction.banner !== HEADER_BANNERS.IMPORT_WARNING &&
        isOverwriting
      ) {
        setImportFormProgress(QUERY_PROGRESS_SUCCEED);
        setHeaderBannerAction({ banner: HEADER_BANNERS.IMPORT_WARNING });
      } else {
        if (isDocsAndExtractActive) {
          if (noDocumentsExtracted) {
            importFormData();
            setHeaderBannerAction({ banner: null });
            setImportFormProgress(QUERY_PROGRESS_SUCCEED);
            return;
          }
          await docsFillForm({
            formData,
            formName,
          });
          return;
        }
        if (isDischargeLetterFormName(formName)) {
          await docsFillForm({
            formData,
            formName,
          });
        } else {
          importFormData();
          setHeaderBannerAction({ banner: null });
          setImportFormProgress(QUERY_PROGRESS_SUCCEED);
        }
      }
    } catch (error) {
      setImportFormProgress(QUERY_PROGRESS_FAILED);
      console.error("error with import form data", error);
    }
  };

  const { handleOpenPdf, handleSendToHis, progressOpenPdf, progressSendToHis } =
    usePdfHandlers({
      auctionForms,
      config,
      files,
      footerScreen,
      formData: formData as DocsFormDataMapped<T>,
      formName,
      formType: form?.form_type,
      isDirty: methods.formState.isDirty,
      patient,
      setFooterBanner,
      setFooterScreen,
    });

  const handleBack = () => {
    setFooterScreen(DOCS_FORM_FOOTER_SCREENS.DEFAULT);
    setFormDataState(undefined);
  };

  const { onSubmit, onSubmitProgress } = useDocsFormOnSubmit({
    auction,
    auctionForms,
    consultation,
    docsFillFormResult,
    formName,
    patient,
    pdfConfig: config.pdfConfig,
    previousFormData: formData,
    sourceInformation,
  });

  const handleSubmit = methods.handleSubmit(
    async (data: DocsFormDataMapped<T>) => {
      switch (footerScreen) {
        case DOCS_FORM_FOOTER_SCREENS.DEFAULT:
          setFooterScreen(DOCS_FORM_FOOTER_SCREENS.SAVE_CONFIRMATION);
          setFormDataState(data);
          break;
        case DOCS_FORM_FOOTER_SCREENS.SAVE_CONFIRMATION: {
          if (!confirmationChecked || !formDataState) return;
          const saved = await onSubmit(
            formDataState,
            methods.formState.isDirty,
          );
          if (!saved) {
            setFooterBanner(FOOTER_BANNERS.SAVE_ERROR);
            handleBack();
            break;
          }
          methods.reset(
            config.getInitialValue({
              auctionForms,
              patient,
              value: formDataState,
            }),
          );
          handleBack();
          setFooterBanner(
            hasPreviousFormValue
              ? FOOTER_BANNERS.OVERWRITE_SUCCESS
              : FOOTER_BANNERS.SAVE_SUCCESS,
          );
          resetDocsFillFormResult();
          break;
        }
      }
    },
  );

  const { deleteFormProgress: handleDeleteProgress, onDelete } =
    useDeleteConfirmation({
      auction,
      consultation,
      form,
      formName,
      patient,
    });

  const handleDelete = async () => {
    try {
      const deleted = await onDelete();
      if (!deleted) {
        setFooterBanner(FOOTER_BANNERS.DELETE_ERROR);
        handleBack();
        return;
      }
      methods.reset(config.getInitialValue({ auctionForms, patient }));
      onAfterDelete?.();
      setFooterBanner(FOOTER_BANNERS.DELETE_SUCCESS);
      setHeaderBannerAction({ banner: null });
      resetDocsFillFormResult();
      handleBack();
    } catch (error) {
      console.error(error);
    }
  };

  const onCloseHeaderBanner = () => {
    setHeaderBannerAction({ banner: null });
  };

  useEffect(() => {
    setConfirmationChecked(false);
  }, [footerScreen]);

  useEffect(() => {
    if (!isDocsAndExtractActive && !isDischargeLetterActive) return;

    if (noDocumentsExtracted) {
      setHeaderBannerAction({ banner: HEADER_BANNERS.NO_FILES_WARNING });
      return;
    }
    setHeaderBannerAction({ banner: null });
  }, [noDocumentsExtracted, isDocsAndExtractActive]);

  const canImportData = useMemo(() => {
    if (isLoading(importFormProgress)) return false;
    if (isDischargeLetterFormName(formName)) {
      if (noDocumentsExtracted) return false;
    }
    return true;
  }, [formName, importFormProgress, noDocumentsExtracted]);

  return {
    canImportData,
    config,
    confirmationChecked,
    footerBanner,
    footerScreen,
    form,
    formData,
    handleBack,
    handleDelete,
    handleDeleteProgress,
    handleOpenPdf,
    handleSendToHis,
    handleSubmit,
    hasPreviousFormValue,
    headerBannerAction,
    importFormData: handleImportData,
    importFormProgress,
    methods,
    noDocumentsExtracted,
    onCloseHeaderBanner,
    onSubmit,
    onSubmitProgress,
    progressOpenPdf,
    progressSendToHis,
    setConfirmationChecked,
    setFooterBanner,
    setFooterScreen,
    setHeaderBannerAction,
    skipRequiredValidation,
    sourceInformation,
  };
}
